import { IPromise, IQService, noop } from "angular";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IMetric, MetricService } from "../okrs/metrics";
import { IItemSelectorOptions, ISelection } from "./components/single-goal-selector.component";
import { GoalService } from "./goal.service";
import { IGoal } from "./models";

export type ICloneTarget = IGoal | IMetric;
export type ICloneTargetType = "goal" | "metric";
export type GoalModalMethod = "clone" | "move" | "select";

interface ISingleGoalSelectorModalParams extends IItemSelectorOptions {
  metric?: IMetric;
  method?: GoalModalMethod;
}

export class GoalModalsService {
  public static $inject = ["$uibModal", "$q", "GoalService", "MetricService", "$rootScope"];

  constructor(
    private $uibModal: IModalService,
    private $q: IQService,
    private goalService: GoalService,
    private metricService: MetricService,
    private $rootScope: ITraceRootScopeService
  ) {}

  public openModalForMove(targetType: ICloneTargetType, target: ICloneTarget, parent?: IGoal): IModalServiceInstance {
    switch (targetType) {
      case "goal": {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const templateUrl = require("./views/modules/move-objective.jade");
        const windowClass = "medium-small no-min-height move-okr";
        return this.openGoalModal(<IGoal>target, templateUrl, windowClass);
      }
      case "metric":
        return this.openSingleGoalSelectorModal({ metric: target as IMetric, method: "move", unselectableId: parent.id, invokedBy: "KR" });
      default:
        // do nothing
        break;
    }
  }

  private openGoalModal(goal: IGoal, templateUrl: string, windowClass = "medium"): IModalServiceInstance {
    return this.$uibModal.open({
      templateUrl,
      windowClass,
      backdrop: false,
      resolve: {
        goal: () => goal,
      },
      controller: "CloneOrMoveGoalCtrl",
    });
  }

  private openSingleGoalSelectorModal(params: ISingleGoalSelectorModalParams): IModalServiceInstance {
    const modalInstance = this.$uibModal.open({
      template: `
        <single-goal-selector
          exclude-session-id='${params.excludeSessionId || ""}'
          planning-session-id='${params.planningSessionId || ""}'
          unselectable-id='${params.unselectableId || ""}'
          invoked-by-id='${params.invokedById || ""}'
          invoked-by='${params.invokedBy || ""}'
          include-metrics='${typeof params.includeMetrics === "boolean" ? params.includeMetrics : ""}'
          show-future-sessions='${!!params.showFutureSessions}'
          show-archived-sessions='${!!params.showArchivedSessions}'
          cancel="$dismiss()"
          select="$close(selection)">
        </single-goal-selector>
      `,
      windowClass: "single-goal-selector-modal",
    });
    modalInstance.result.then((selection: ISelection) => {
      if (selection.type === "goal" && params.method !== "select" && params.metric) {
        this.metricService.cloneOrMoveMetric(params.metric, selection.goal, params.method);
      }
    }, noop);
    return modalInstance;
  }

  public openGoalSelectModal(options: IItemSelectorOptions): IPromise<ISelection> {
    const modalInstance = this.openSingleGoalSelectorModal({ method: "select", ...options });
    return modalInstance.result;
  }

  public selectGoal(options: IItemSelectorOptions): IPromise<IGoal> {
    return this.openGoalSelectModal(options).then((selection: ISelection) => {
      return this.$rootScope.traceAction("select_goal", () => {
        if (selection.type === "goal") {
          return this.goalService.getGoal(selection.goal.id);
        }

        return this.$q.reject(`Invalid selection '${selection.type}', expected goal`);
      });
    });
  }
}
